import { Component, OnDestroy, AfterViewInit, ViewChild, ChangeDetectorRef } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { ActivatedRoute, Router } from '@angular/router';

import { switchMap, tap } from 'rxjs/operators';
import { BehaviorSubject, Subscription } from 'rxjs';

import { TmtLoggerService } from 'tmt-logger';

import { TestCase } from 'src/models/testCase';
import { TestRunBaseService } from 'src/services/test-run.base.service';
import { TestRunViewModel } from 'src/models/testRun';

@Component({
	selector: 'app-test-cases',
	templateUrl: './test-cases.component.html',
	styleUrls: ['./test-cases.component.scss'],
})
export class TestCasesComponent implements OnDestroy, AfterViewInit {
	private subscription: Subscription;

	private subscriptiontc: Subscription;

	displayedColumns: string[] = ['itemNo', 'name', 'type', 'environment', 'testCaseVersion', 'result', 'troubleReports', 'resultComment', 'executionTime', 'regBy'];

	dataSource: MatTableDataSource<TestRunViewModel>;

	@ViewChild(MatPaginator) public set matPaginator(paginator: MatPaginator) {
		if (paginator && this.testCases) this.dataSource.paginator = paginator;
	}

	@ViewChild(MatSort) public set sort(sort: MatSort) {
		if (this.dataSource) {
			this.dataSource.sort = sort;
		}
	}

	maxall = 10;

	testCases: TestRunViewModel[];

	testCaseNoResults: TestCase;

	gettingTestRuns = new BehaviorSubject<boolean>(false);

	constructor(
		private route: ActivatedRoute,
		private router: Router,
		private loggerService: TmtLoggerService,
		private testRunBaseService: TestRunBaseService,
		private cdr: ChangeDetectorRef,
	) {}

	// sorting and paging does not work unless the fetch of test cases also is done in AfterViewInit
	ngAfterViewInit(): void {
		this.getTestCases();
	}

	public getTestCases(): void {
		this.subscription = this.route.url
			.pipe(
				tap(() => {
					this.gettingTestRuns.next(true);
					this.cdr.detectChanges();
				}),
				switchMap(urlSegment => this.testRunBaseService.getTestRunsByTestCaseUid(urlSegment[1].path)),
			)
			.subscribe(
				testcasedata => {
					this.testCases = testcasedata;
					const dataSource = new MatTableDataSource<TestRunViewModel>(this.testCases);
					// set a new sortingDataAccessor to be able to sort nested objects
					dataSource.sortingDataAccessor = (item, property) => {
						if (item.testRunType === 410) {
							switch (property) {
								case 'itemNo':
									return Number(item.testSpecification?.itemNo.split('-').pop() ?? 0);
								case 'name':
									return item.testSpecification?.name;
								case 'type':
									return 'Test specification';
								case 'environment':
									return item.testRun?.environment;
								case 'testCaseVersion':
									return item.testRun?.testResults[0]?.testCaseVersion;
								case 'result':
									return item.testRun?.testResults[0]?.result;
								case 'troubleReports':
									return item.testRun?.testResults[0]?.troubleReports.length;
								case 'resultComment':
									return item.testRun?.testResults[0]?.resultComment;
								case 'executionTime':
									return item.testRun?.executionTime;
								case 'regBy':
									return item.testRun?.regBy;
								default:
									return item[property];
							}
						} else {
							switch (property) {
								case 'itemNo':
									return Number(item.testSuite?.itemNo.split('-').pop() ?? 0);
								case 'name':
									return item.testSuite?.name;
								case 'type':
									return 'Test suite';
								case 'environment':
									return item.testRun?.environment;
								case 'testCaseVersion':
									return item.testRun?.testResults[0]?.testCaseVersion;
								case 'result':
									return item.testRun?.testResults[0]?.result;
								case 'troublereports':
									return item.testRun?.testResults[0]?.troubleReports?.length;
								case 'resultComment':
									return item.testRun?.testResults[0]?.resultComment;
								case 'executionTime':
									return item.testRun?.executionTime;
								case 'regBy':
									return item.testRun?.regBy;
								default:
									return item[property];
							}
						}
					};
					// set a new filterPredicate function to be able to filter nested objects
					dataSource.filterPredicate = (data, filter: string) => {
						const accumulator = (currentTerm, key) => {
							return this.nestedFilterCheck(currentTerm, data, key);
						};
						const dataStr = Object.keys(data).reduce(accumulator, '').toLowerCase();
						const transformedFilter = filter.trim().toLowerCase();
						return dataStr.indexOf(transformedFilter) !== -1;
					};
					this.dataSource = dataSource;
					this.gettingTestRuns.next(false);
					this.cdr.detectChanges();
				},
				error => {
					this.loggerService.logError(error);
					this.gettingTestRuns.next(false);
				},
			);
	}

	private nestedFilterCheck(search, data, key) {
		if (typeof data[key] === 'object') {
			for (const k in data[key]) {
				if (data[key][k] !== null) {
					search = this.nestedFilterCheck(search, data[key], k);
				}
			}
		} else {
			search += data[key];
		}
		return search;
	}

	public applyFilter(event: Event) {
		const filterValue = (event.target as HTMLInputElement).value;
		this.dataSource.filter = filterValue.trim().toLowerCase();
	}

	public getPageSizeOptions(): number[] {
		if (this.dataSource && this.dataSource.paginator && this.dataSource.paginator.length > this.maxall) {
			return [10, 20, this.dataSource.paginator.length];
		} else {
			return [10, 20];
		}
	}

	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}

	public navigateTo(row: any) {
		this.router.navigate(['/testresult/' + row.testRun.testResults[0].testResultUid]);
	}
}
